// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useMemo } from 'react';
import { Marker } from 'react-map-gl';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { useAppSelector } from 'Hooks';
import { sharedPrefixLength, truncateMiddle } from 'Utils/fileNameUtils';
import type { FileFootprint } from '../footprintSelectors';
import { nwCorner } from './footprintGeoJson';
import stylesDef from './styles';

const CHIP_MAX = 24;
const CAPTION_MAX = 40;

interface ComponentProps {
  footprints: FileFootprint[];
  footprintsOn: boolean;
}

// HTML Markers, not symbol layers: the no-basemap map style carries no glyphs,
// and Markers survive the streets/satellite style swap.
// Chips label every tile while the switch is on; a map-sourced hover upgrades
// that tile's chip to a longer caption (sidebar hover highlights without one).
export const FootprintCornerLabels: React.FC<ComponentProps> = ({ footprints, footprintsOn }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const hovered = useAppSelector((state) => state.fileSelection.hovered);

  // Chips drop the prefix shared by every same-kind file ("…3_002.tif"), so a
  // full grid stays readable; the hover caption keeps the real name.
  const prefixLenByKind = useMemo(() => {
    const lens = new Map<FileSelectionKind, number>();
    footprints.forEach((fp) => {
      if (lens.has(fp.kind)) return;
      lens.set(fp.kind, sharedPrefixLength(footprints.filter((f) => f.kind === fp.kind).map((f) => f.name)));
    });
    return lens;
  }, [footprints]);

  const chipText = (fp: FileFootprint): string => {
    const prefixLen = prefixLenByKind.get(fp.kind) || 0;
    if (!prefixLen) return truncateMiddle(fp.name, CHIP_MAX);
    return `…${truncateMiddle(fp.name.slice(prefixLen), CHIP_MAX - 1)}`;
  };

  // Captions follow hover from EITHER side (D71): a sidebar row hover names
  // its box on the map just like a map hover does, so the pane<->map link
  // reads the same in both directions.
  const hoveredKeys = new Set(
    hovered ? hovered.files.map((f) => `${f.kind}-${f.id}`) : [],
  );

  // Every hovered footprint gets a caption at its own NW corner; overlapping
  // footprints with the SAME corner (stacked tile sets) merge into one card,
  // one name per line, instead of piling markers on top of each other.
  const captionGroups = new Map<string, { longitude: number; latitude: number; names: string[] }>();
  footprints.forEach((fp) => {
    if (!hoveredKeys.has(`${fp.kind}-${fp.id}`)) return;
    const [longitude, latitude] = nwCorner(fp.bounds);
    const key = `${longitude}|${latitude}`;
    const group = captionGroups.get(key) || { longitude, latitude, names: [] };
    group.names.push(truncateMiddle(fp.name, CAPTION_MAX));
    captionGroups.set(key, group);
  });

  return (
    <>
      {footprintsOn && footprints.map((fp) => {
        if (hoveredKeys.has(`${fp.kind}-${fp.id}`)) return null;
        const [longitude, latitude] = nwCorner(fp.bounds);
        return (
          <Marker key={`${fp.kind}-${fp.id}`} longitude={longitude} latitude={latitude} anchor="top-left">
            <Box sx={styles.cornerLabel}>
              <Typography variant="body2" sx={styles.cornerLabelText}>
                {chipText(fp)}
              </Typography>
            </Box>
          </Marker>
        );
      })}
      {Array.from(captionGroups.entries()).map(([key, group]) => (
        <Marker key={key} longitude={group.longitude} latitude={group.latitude} anchor="top-left">
          <Box sx={[styles.cornerLabel, styles.cornerLabelHovered]}>
            {group.names.map((name) => (
              <Typography key={name} variant="body2" sx={styles.cornerLabelText}>
                {name}
              </Typography>
            ))}
          </Box>
        </Marker>
      ))}
    </>
  );
};

export default FootprintCornerLabels;
